import { proxyCustomElement, HTMLElement, h, Host, Fragment } from '@stencil/core/internal/client';
import { d as defineCustomElement$5 } from './close-button.js';
import { d as defineCustomElement$4 } from './icon.js';
import { d as defineCustomElement$3 } from './tag.js';
import { d as defineCustomElement$2 } from './tooltip.js';
import { d as defineCustomElement$1 } from './tooltip-label.js';
import { d as debounce } from './debounce.js';

const scrollIndicatorCss = ":host{--push-top:calc(var(--fade-size-top)/2);--push-bottom:calc(var(--fade-size-bottom)/2);-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex:1;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;max-height:100%;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{display:flex;flex:1;flex-direction:column;-webkit-mask-image:linear-gradient(to bottom,#0000 0,#0000 var(--push-top,0),#000 calc(var(--fade-size-top, 0) + var(--push-top, 0)),#000 calc(100% - var(--push-bottom, 0) - var(--fade-size-bottom, 0)),#0000 calc(100% - var(--push-bottom, 0)));mask-image:linear-gradient(to bottom,#0000 0,#0000 var(--push-top,0),#000 calc(var(--fade-size-top, 0) + var(--push-top, 0)),#000 calc(100% - var(--push-bottom, 0) - var(--fade-size-bottom, 0)),#0000 calc(100% - var(--push-bottom, 0)));overflow-x:hidden;overflow-y:auto;scrollbar-width:thin}.tag{left:.125rem;opacity:1;position:absolute;transition:opacity .2s linear,top .25s ease,bottom .25s ease;z-index:1}.tag-up{top:0}.tag-down{bottom:.125rem}:host([data-at-top]) .tag-up{display:none;opacity:0;top:0}:host([data-at-bottom]) .tag-down{bottom:0;display:none;opacity:0}";
const WuiScrollIndicatorStyle0 = scrollIndicatorCss;

const SCROLL_FADE_SIZE = 36;
const SCROLL_TAG_TOLERANCE = 20;
const WuiScrollIndicator = /*@__PURE__*/ proxyCustomElement(class WuiScrollIndicator extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.handleScroll = ({ currentTarget }) => {
            if (currentTarget !== this.scrollElm) {
                return;
            }
            this.updateElm();
        };
        this.setRef = (elm) => {
            this.scrollElm = elm;
            this.updateElm();
            this.scrollElm?.removeEventListener('scroll', this.handleScroll);
            this.scrollElm?.addEventListener('scroll', this.handleScroll);
        };
        this.handleWindowResize = debounce(() => {
            this.updateElm();
        }, 300);
        this.isScrollable = false;
        this.atTop = undefined;
        this.atBottom = undefined;
        this.showTagsLabel = true;
    }
    updateElm() {
        requestAnimationFrame(() => {
            if (!this.scrollElm) {
                return;
            }
            const { scrollHeight, clientHeight, scrollTop } = this.scrollElm;
            this.isScrollable = scrollHeight > clientHeight;
            if (!this.isScrollable) {
                // Remove all classes
                this.elm.style.setProperty('--fade-size-top', '0');
                this.elm.style.setProperty('--fade-size-bottom', '0');
                return;
            }
            const scrollBottom = scrollHeight - clientHeight - scrollTop;
            this.atTop = scrollTop <= SCROLL_TAG_TOLERANCE;
            this.atBottom = scrollBottom <= SCROLL_TAG_TOLERANCE;
            this.elm.style.setProperty('--fade-size-top', `${Math.min(scrollTop, SCROLL_FADE_SIZE)}px`);
            this.elm.style.setProperty('--fade-size-bottom', `${Math.min(scrollBottom, SCROLL_FADE_SIZE)}px`);
        });
    }
    connectedCallback() {
        window.addEventListener('resize', this.handleWindowResize);
    }
    disconnectedCallback() {
        window.removeEventListener('resize', this.handleWindowResize);
    }
    render() {
        const tagLabel = this.showTagsLabel ? 'Scroll for more' : undefined;
        return (h(Host, { key: 'c3d9cbd1d5bd59f42d24baa638f7cff300b8b60b', "data-at-top": this.atTop || undefined, "data-at-bottom": this.atBottom || undefined, "data-tag-centered": this.showTagsLabel ? undefined : true }, h("div", { key: '534b6170a2da71ef40b33479b01c2a6f1dda5bee', part: "inner", class: "inner", ref: this.setRef }, h("slot", { key: '96d85e50e3da19392ddb577efdd84a48868e9edb' })), this.isScrollable && (h(Fragment, { key: '1d5ad208e779113eb9308318069c6b9e86bb8cc6' }, h("wui-tag", { key: '1cf829f17d6ac2b00bb49d8c365c50e4f2c431f3', class: "tag tag-up", label: tagLabel, icon: "arrow-up", action: true, onClick: ({ currentTarget }) => {
                this.scrollElm?.scrollTo({ top: 0, behavior: 'instant' });
                currentTarget?.blur();
            } }), h("wui-tag", { key: '2f87ee85a07b1e5215be04270ffad6807f6bd6b0', class: "tag tag-down", label: tagLabel, icon: "arrow-down", action: true, onClick: ({ currentTarget }) => {
                this.scrollElm?.scrollTo({ top: 99999999, behavior: 'instant' });
                currentTarget?.blur();
            } })))));
    }
    get elm() { return this; }
    static get style() { return WuiScrollIndicatorStyle0; }
}, [1, "wui-scroll-indicator", {
        "showTagsLabel": [4, "show-tags-label"],
        "isScrollable": [32],
        "atTop": [32],
        "atBottom": [32]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-scroll-indicator", "wui-close-button", "wui-icon", "wui-tag", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-scroll-indicator":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiScrollIndicator);
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tag":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiScrollIndicator as W, defineCustomElement as d };

//# sourceMappingURL=scroll-indicator.js.map